import type { WorkingReportKind } from "@/store/ide-v2-slice";

export interface WorkingReportDocxModel {
  eyebrow: string;
  title: string;
  status: string;
  description: string;
  sourceFrames: Array<{
    name: string;
    detail: string;
  }>;
  metrics: Array<{
    label: string;
    value: string;
  }>;
  sections: Array<{
    title: string;
    body: string;
    rows: Array<{
      label: string;
      value: string;
    }>;
  }>;
  checks: string[];
}

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const FILES = {
  contentTypes: "[Content_Types].xml",
  rootRels: "_rels/.rels",
  document: "word/document.xml",
  styles: "word/styles.xml",
  core: "docProps/core.xml",
  app: "docProps/app.xml",
};

export function downloadWorkingReportDocx(
  report: WorkingReportDocxModel,
  kind: WorkingReportKind,
): { fileName: string; bytes: number } {
  const bytes = buildWorkingReportDocx(report);
  const fileName = `${safeFileName(report.title || kind)}.docx`;
  if (typeof document === "undefined" || typeof URL === "undefined") {
    return { fileName, bytes: bytes.byteLength };
  }
  const blob = new Blob([toArrayBuffer(bytes)], { type: DOCX_MIME });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  // Keep the object URL alive until the browser has consumed the synthetic
  // anchor navigation. Immediate revocation is fast enough for jsdom but can
  // race real Chromium and silently drop the download.
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 1_000);
  return { fileName, bytes: bytes.byteLength };
}

export function buildWorkingReportDocx(report: WorkingReportDocxModel): Uint8Array {
  return createZip([
    { name: FILES.contentTypes, data: xmlBytes(contentTypesXml()) },
    { name: FILES.rootRels, data: xmlBytes(rootRelsXml()) },
    { name: FILES.document, data: xmlBytes(documentXml(report)) },
    { name: FILES.styles, data: xmlBytes(stylesXml()) },
    { name: FILES.core, data: xmlBytes(coreXml(report)) },
    { name: FILES.app, data: xmlBytes(appXml()) },
  ]);
}

function contentTypesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`;
}

function rootRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`;
}

function coreXml(report: WorkingReportDocxModel): string {
  const now = new Date().toISOString();
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>${xml(report.title)}</dc:title>
  <dc:creator>Coogo Studio</dc:creator>
  <cp:lastModifiedBy>Coogo Studio</cp:lastModifiedBy>
  <dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created>
  <dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified>
</cp:coreProperties>`;
}

function appXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>Coogo Studio</Application>
  <DocSecurity>0</DocSecurity>
  <ScaleCrop>false</ScaleCrop>
</Properties>`;
}

function stylesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:rPr>
      <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:eastAsia="Apple SD Gothic Neo"/>
      <w:sz w:val="21"/>
      <w:color w:val="0F172A"/>
    </w:rPr>
    <w:pPr>
      <w:spacing w:after="120" w:line="276" w:lineRule="auto"/>
    </w:pPr>
  </w:style>
</w:styles>`;
}

function documentXml(report: WorkingReportDocxModel): string {
  const rows = [
    paragraph(report.eyebrow.toUpperCase(), { size: 18, color: "64748B", bold: true, after: 80 }),
    paragraph(report.title, { size: 32, color: "0F172A", bold: true, after: 80 }),
    paragraph(report.description, { size: 21, color: "475569", after: 180 }),
    keyValueTable([
      ["Status", report.status],
      ["Generated by", "Coogo Studio"],
      ["Output", "DOCX"],
    ]),
    heading("Metrics"),
    table(
      ["Metric", "Value"],
      report.metrics.map((item) => [item.label, item.value]),
      [4200, 2160],
    ),
    heading("Source Frames"),
    table(
      ["Frame", "Detail"],
      report.sourceFrames.map((frame) => [frame.name, frame.detail]),
      [4200, 2160],
    ),
    ...report.sections.flatMap((section) => [
      heading(section.title),
      paragraph(section.body, { size: 21, color: "334155", after: 90 }),
      table(
        ["Item", "Value"],
        section.rows.map((row) => [row.label, row.value]),
        [2600, 3760],
      ),
    ]),
    heading("User Checks"),
    ...report.checks.map((check) => paragraph(`• ${check}`, { size: 21, color: "334155", after: 70 })),
  ];

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${rows.join("\n")}
    <w:sectPr>
      <w:pgSz w:w="12240" w:h="15840"/>
      <w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="708" w:footer="708" w:gutter="0"/>
    </w:sectPr>
  </w:body>
</w:document>`;
}

function heading(text: string): string {
  return paragraph(text, { size: 26, color: "1F4E79", bold: true, before: 220, after: 100 });
}

function paragraph(
  text: string,
  options: {
    size?: number;
    color?: string;
    bold?: boolean;
    before?: number;
    after?: number;
  } = {},
): string {
  const size = options.size ?? 21;
  const color = options.color ?? "0F172A";
  const before = options.before ?? 0;
  const after = options.after ?? 120;
  return `<w:p>
    <w:pPr><w:spacing w:before="${before}" w:after="${after}" w:line="276" w:lineRule="auto"/></w:pPr>
    <w:r>
      <w:rPr>${options.bold ? "<w:b/>" : ""}<w:color w:val="${color}"/><w:sz w:val="${size}"/><w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:eastAsia="Apple SD Gothic Neo"/></w:rPr>
      <w:t xml:space="preserve">${xml(text)}</w:t>
    </w:r>
  </w:p>`;
}

function keyValueTable(rows: string[][]): string {
  return table(["Field", "Value"], rows, [2100, 4260]);
}

function table(headers: string[], rows: string[][], widths: number[]): string {
  const grid = widths.map((width) => `<w:gridCol w:w="${width}"/>`).join("");
  const headerRow = tableRow(headers, widths, true);
  const bodyRows = rows.map((row) => tableRow(row, widths, false)).join("\n");
  const totalWidth = widths.reduce((sum, width) => sum + width, 0);
  return `<w:tbl>
    <w:tblPr>
      <w:tblW w:w="${totalWidth}" w:type="dxa"/>
      <w:tblInd w:w="120" w:type="dxa"/>
      <w:tblLayout w:type="fixed"/>
      <w:tblBorders>
        <w:top w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
        <w:left w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
        <w:bottom w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
        <w:right w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
        <w:insideH w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
        <w:insideV w:val="single" w:sz="4" w:space="0" w:color="D7DEE8"/>
      </w:tblBorders>
      <w:tblCellMar>
        <w:top w:w="90" w:type="dxa"/>
        <w:bottom w:w="90" w:type="dxa"/>
        <w:start w:w="150" w:type="dxa"/>
        <w:end w:w="150" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
    <w:tblGrid>${grid}</w:tblGrid>
    ${headerRow}
    ${bodyRows}
  </w:tbl>`;
}

function tableRow(cells: string[], widths: number[], header: boolean): string {
  return `<w:tr>${cells
    .map((cell, index) => tableCell(cell, widths[index] ?? 2200, header))
    .join("")}</w:tr>`;
}

function tableCell(text: string, width: number, header: boolean): string {
  return `<w:tc>
    <w:tcPr>
      <w:tcW w:w="${width}" w:type="dxa"/>
      <w:shd w:fill="${header ? "EAF2FB" : "FFFFFF"}"/>
      <w:vAlign w:val="center"/>
      <w:tcMar>
        <w:top w:w="90" w:type="dxa"/>
        <w:bottom w:w="90" w:type="dxa"/>
        <w:start w:w="150" w:type="dxa"/>
        <w:end w:w="150" w:type="dxa"/>
      </w:tcMar>
    </w:tcPr>
    ${paragraph(text, {
      size: header ? 18 : 19,
      color: header ? "1F4E79" : "0F172A",
      bold: header || text.toLowerCase() === "pass",
      after: 0,
    })}
  </w:tc>`;
}

function xmlBytes(value: string): Uint8Array {
  return new TextEncoder().encode(value.trim());
}

function xml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function safeFileName(value: string): string {
  const ascii = value
    .normalize("NFKD")
    .replace(/[^\w\s.-]/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
  return ascii || "studio-report";
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(out).set(bytes);
  return out;
}

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

function createZip(entries: ZipEntry[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  const centralChunks: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = new TextEncoder().encode(entry.name);
    const crc = crc32(entry.data);
    const local = new Uint8Array(30 + nameBytes.byteLength);
    const view = new DataView(local.buffer);
    writeLocalHeader(view, entry.data.byteLength, crc, nameBytes.byteLength);
    local.set(nameBytes, 30);
    chunks.push(local, entry.data);

    const central = new Uint8Array(46 + nameBytes.byteLength);
    const centralView = new DataView(central.buffer);
    writeCentralHeader(
      centralView,
      entry.data.byteLength,
      crc,
      nameBytes.byteLength,
      offset,
    );
    central.set(nameBytes, 46);
    centralChunks.push(central);
    offset += local.byteLength + entry.data.byteLength;
  }

  const centralOffset = offset;
  const centralSize = centralChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, centralOffset, true);

  return concatBytes([...chunks, ...centralChunks, end]);
}

function writeLocalHeader(
  view: DataView,
  size: number,
  crc: number,
  nameLength: number,
): void {
  const { date, time } = dosDateTime();
  view.setUint32(0, 0x04034b50, true);
  view.setUint16(4, 20, true);
  view.setUint16(6, 0x0800, true);
  view.setUint16(8, 0, true);
  view.setUint16(10, time, true);
  view.setUint16(12, date, true);
  view.setUint32(14, crc, true);
  view.setUint32(18, size, true);
  view.setUint32(22, size, true);
  view.setUint16(26, nameLength, true);
}

function writeCentralHeader(
  view: DataView,
  size: number,
  crc: number,
  nameLength: number,
  offset: number,
): void {
  const { date, time } = dosDateTime();
  view.setUint32(0, 0x02014b50, true);
  view.setUint16(4, 20, true);
  view.setUint16(6, 20, true);
  view.setUint16(8, 0x0800, true);
  view.setUint16(10, 0, true);
  view.setUint16(12, time, true);
  view.setUint16(14, date, true);
  view.setUint32(16, crc, true);
  view.setUint32(20, size, true);
  view.setUint32(24, size, true);
  view.setUint16(28, nameLength, true);
  view.setUint32(42, offset, true);
}

function dosDateTime(): { date: number; time: number } {
  const now = new Date();
  const year = Math.max(now.getFullYear(), 1980);
  return {
    date: ((year - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate(),
    time:
      (now.getHours() << 11) |
      (now.getMinutes() << 5) |
      Math.floor(now.getSeconds() / 2),
  };
}

const CRC_TABLE = new Uint32Array(256).map((_, index) => {
  let c = index;
  for (let k = 0; k < 8; k += 1) {
    c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  }
  return c >>> 0;
});

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}
